"use client";

import React from "react";
import { Stethoscope, Sparkles, Activity, HeartPulse, ChevronDown, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "dentures",
  title: "Premium Dentures",
  description: "Affordable full dentures, partial dentures, snap-on dentures, and same-day dentures designed for comfort, function, and natural appearance.",
  iconName: "Sparkles"
}, {
  id: "implants",
  title: "Dental Implants",
  description: "Permanent tooth replacement solutions including single dental implants, implant-supported dentures, and full mouth dental implants (All-on-4 / All-on-X).",
  iconName: "Activity"
}, {
  id: "extractions",
  title: "Gentle Extractions",
  description: "Wisdom teeth removal, surgical extractions, and comfortable procedures with IV sedation dentistry in El Monte.",
  iconName: "Stethoscope"
}, {
  id: "sedation",
  title: "Sedation Dentistry",
  description: "Relax and sleep comfortably during your procedure with IV sedation dentistry. Ideal for patients with dental anxiety or those undergoing extractions, implants, or full mouth reconstruction.",
  iconName: "HeartPulse"
}];
const procedures = [{
  serviceId: "dentures",
  name: "Full & Partial Custom Dentures"
}, {
  serviceId: "dentures",
  name: "Same-Day Denture Repairs"
}, {
  serviceId: "dentures",
  name: "Professional Reline Services"
}, {
  serviceId: "implants",
  name: "Single Tooth Dental Implants"
}, {
  serviceId: "implants",
  name: "All-on-4 Smile Restoration"
}, {
  serviceId: "implants",
  name: "Implant-Supported Bridges"
}, {
  serviceId: "extractions",
  name: "Emergency Tooth Extractions"
}, {
  serviceId: "extractions",
  name: "Wisdom Tooth Management"
}, {
  serviceId: "extractions",
  name: "Post-Extraction Care Plans"
}, {
  serviceId: "sedation",
  name: "Oral Conscious Sedation"
}, {
  serviceId: "sedation",
  name: "Nitrous Oxide (Laughing Gas)"
}, {
  serviceId: "sedation",
  name: "Comfort-First Monitoring"
}];
const iconMap: Record<string, React.ElementType> = {
  Stethoscope,
  Sparkles,
  Activity,
  HeartPulse
};
export default function OurSpecializedCare() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="75655" id="our-clinical-specializations" className="bg-secondary py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-primary mb-6 tracking-tight">
              Our Clinical Specializations
            </h2>
            <p className="text-lg text-foreground/80 max-w-2xl leading-relaxed">
              At Smart Choice Denture &amp; Implants, we specialize in affordable dentures and dental implants in El Monte, CA. Whether you need same-day dentures, full mouth dental implants, or tooth extractions with IV sedation, our team provides fast, high-quality care in one convenient location. We proudly serve patients throughout the San Gabriel Valley looking for affordable tooth replacement options.
            </p>
          </div>
          <div className="relative group">
            <div className="absolute -inset-4 bg-primary/5 rounded-[2rem] blur-2xl transition-all duration-500 group-hover:bg-primary/10" />
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617360/img/happy_senior_lady_smiling.png" alt="Happy senior patient smiling" className="relative rounded-[2rem] shadow-2xl w-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-[1.02]" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, i) => {
          const IconComponent = iconMap[service.iconName];
          const serviceProcedures = procedures.filter(p => p.serviceId === service.id);
          return <Collapsible key={service.id} data-index={i} className={`group transition-all duration-1000 delay-[${i * 150}ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="h-full border-border/60 bg-card shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 rounded-[1.5rem] overflow-hidden">
                  <CollapsibleTrigger className="w-full text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-[1.5rem]">
                    <CardHeader className="p-8 md:p-10 flex flex-col sm:flex-row sm:items-start gap-6">
                      <div className="bg-primary/5 text-primary p-5 rounded-2xl shrink-0 w-fit transition-colors group-hover:bg-primary/10">
                        <IconComponent className="size-12 stroke-[1.5]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4 mb-3">
                          <CardTitle className="text-2xl font-serif font-medium text-foreground">
                            {service.title}
                          </CardTitle>
                          <div className="bg-secondary text-secondary-foreground p-2 rounded-full shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-180">
                            <ChevronDown className="size-5" />
                          </div>
                        </div>
                        <CardDescription className="text-base text-muted-foreground leading-relaxed">
                          {service.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-4 data-[state=open]:slide-in-from-top-4 transition-all duration-300 ease-in-out">
                    <CardContent className="px-8 pb-8 md:px-10 md:pb-10 pt-0 sm:pl-[7.5rem]">
                      <div className="h-px w-full bg-border/40 mb-6" />
                      <ul className="space-y-4">
                        {serviceProcedures.map((procedure, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-foreground/90">
                            <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" />
                            <span className="text-base font-medium">{procedure.name}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>;
        })}
        </div>
      </div>
    </section>;
}